import React, { useState, useRef, useCallback } from 'react';
import { Paperclip, X, FileIcon, Upload, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { socket } from '../services/socket';
import { useStore } from '../store/useStore';

interface FileUploadProps {
    isOpen: boolean;
    onClose: () => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const CHUNK_SIZE = 64 * 1024; // 64KB chunks

export const FileUpload: React.FC<FileUploadProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);

    const currentPartner = useStore((state) => state.currentPartner);
    const { addMessage, user } = useStore();

    const handleFileSelect = useCallback((file: File) => {
        setError(null);

        if (file.size > MAX_FILE_SIZE) {
            setError(t('file.too_large') || `Arquivo muito grande. Máximo: ${MAX_FILE_SIZE / 1024 / 1024}MB`);
            return;
        }

        setSelectedFile(file);

        // Create preview for images
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            setPreview(null);
        }
    }, [t]);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const sendFile = async () => {
        if (!selectedFile || !currentPartner) return;

        setUploading(true);
        setProgress(0);

        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const fileData = e.target?.result as string;
                const totalChunks = Math.ceil(fileData.length / CHUNK_SIZE);

                // Send file offer
                socket.emit('file-offer', {
                    fileName: selectedFile.name,
                    fileSize: selectedFile.size,
                    fileType: selectedFile.type,
                    to: currentPartner.socketId
                });

                // Send file in chunks
                for (let i = 0; i < totalChunks; i++) {
                    const start = i * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, fileData.length);
                    const chunk = fileData.slice(start, end);

                    socket.emit('file-data', {
                        data: chunk,
                        to: currentPartner.socketId,
                        chunkIndex: i,
                        totalChunks,
                        fileName: selectedFile.name
                    });

                    setProgress(((i + 1) / totalChunks) * 100);

                    // Small delay to prevent overwhelming the connection
                    await new Promise(resolve => setTimeout(resolve, 10));
                }

                // Send completion message
                socket.emit('file-complete', {
                    to: currentPartner.socketId,
                    fileName: selectedFile.name
                });

                // Add message to local chat
                const message = {
                    id: Math.random().toString(36).substr(2, 9),
                    senderId: user!.id,
                    content: '',
                    timestamp: Date.now(),
                    type: 'file' as const,
                    fileName: selectedFile.name,
                    fileData: fileData,
                    fileType: selectedFile.type,
                    fileSize: selectedFile.size
                };
                addMessage(message);

                // Also send via message event for the partner
                socket.emit('message', {
                    to: currentPartner.socketId,
                    message: '',
                    type: 'file',
                    fileName: selectedFile.name,
                    fileData: fileData,
                    fileType: selectedFile.type,
                    fileSize: selectedFile.size
                });

                setUploading(false);
                setProgress(100);

                // Close after short delay
                setTimeout(() => {
                    onClose();
                    setSelectedFile(null);
                    setPreview(null);
                    setProgress(0);
                }, 500);
            };

            reader.readAsDataURL(selectedFile);
        } catch (err) {
            console.error('Error sending file:', err);
            setError(t('file.send_error') || 'Erro ao enviar arquivo');
            setUploading(false);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 slide-in">
            <div className="bg-gray-800 rounded-lg max-w-md w-full p-6 fade-in">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-white">
                        {t('file.attach') || 'Anexar arquivo'}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {error && (
                    <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 mb-4 flex items-center">
                        <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                        <span className="text-red-400 text-sm">{error}</span>
                    </div>
                )}

                <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging
                            ? 'border-yellow-400 bg-yellow-400/10'
                            : 'border-gray-600 hover:border-gray-500'
                        }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {selectedFile ? (
                        <div>
                            {preview ? (
                                <img
                                    src={preview}
                                    alt="Preview"
                                    className="max-h-40 mx-auto rounded-lg mb-4"
                                />
                            ) : (
                                <FileIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            )}
                            <p className="text-white font-medium">{selectedFile.name}</p>
                            <p className="text-gray-400 text-sm">{formatFileSize(selectedFile.size)}</p>
                            <button
                                onClick={() => {
                                    setSelectedFile(null);
                                    setPreview(null);
                                }}
                                className="mt-2 text-red-400 hover:text-red-300 text-sm"
                            >
                                {t('file.remove') || 'Remover'}
                            </button>
                        </div>
                    ) : (
                        <>
                            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-300 mb-2">
                                {t('file.drag_drop') || 'Arraste e solte um arquivo aqui'}
                            </p>
                            <p className="text-gray-500 text-sm mb-4">
                                {t('file.or') || 'ou'}
                            </p>
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="bg-yellow-500 text-black px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors"
                            >
                                {t('file.browse') || 'Selecionar arquivo'}
                            </button>
                            <p className="text-gray-500 text-xs mt-4">
                                {t('file.size_limit') || 'Tamanho máximo: 10MB'}
                            </p>
                        </>
                    )}
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleInputChange}
                    className="hidden"
                />

                {uploading && (
                    <div className="mt-4">
                        <div className="bg-gray-700 rounded-full h-2 overflow-hidden">
                            <div
                                className="bg-yellow-400 h-full transition-all duration-200"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <p className="text-gray-400 text-sm text-center mt-2">
                            {t('file.sending') || 'Enviando...'} {Math.round(progress)}%
                        </p>
                    </div>
                )}

                <div className="flex space-x-3 mt-6">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2 px-4 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
                    >
                        {t('report.cancel') || 'Cancelar'}
                    </button>
                    <button
                        onClick={sendFile}
                        disabled={!selectedFile || uploading || !currentPartner}
                        className="flex-1 py-2 px-4 bg-yellow-500 text-black rounded hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Paperclip className="w-4 h-4 inline mr-2" />
                        {t('file.send') || 'Enviar'}
                    </button>
                </div>
            </div>
        </div>
    );
};
